import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { IconButton } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  Search as PageviewIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import QCXSimpleConsultPageTemplate from '../../../../../templates/simple-consult-page/QCXSimpleConsultPageTemplate';
import TabelaSdaContext from '../../../../../contexts/registrations/tabela-sda/TabelaSdaContext';
import {
  activateByIdAsync,
  changeToConsultMode,
  changeToCreateMode,
  fetchAllAsync,
  inactivateByIdAsync,
  resetBackgroundMode,
  resetMode,
  resetModel,
  resetSubMode,
  selectTabelasSda,
} from '../../../../../features/tabela-sda/tabelaSdaSlice';
import {
  ALTERNATIVE_LOADING_STATUS,
} from '../../../../../features/status';
import useOperationController from '../../../../../utils/hooks/store/operation/useOperationController';
import { resetList as resetListDespachante } from '../../../../../features/despachante/despachanteSlice';
import { userHasDeleteRole } from '../../../../../components/perfil/perfil-utils';

export default function TabelaSDAConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();

  const history = useHistory();

  const {
    status,
  } = useContext(TabelaSdaContext);

  const dispatch = useDispatch();

  const tabelasSda = useSelector(selectTabelasSda);

  const isAlternativeLoading = useMemo(() => status === ALTERNATIVE_LOADING_STATUS, [status]);

  const fetchAll = async () => {
    dispatch(fetchAllAsync());
  };

  const resetModes = useCallback(() => {
    dispatch(resetMode());
    dispatch(resetBackgroundMode());
    dispatch(resetSubMode());
  }, []);

  const resetModels = useCallback(() => {
    dispatch(resetModel());
  }, []);

  const resetDependencies = useCallback(() => {
    dispatch(resetListDespachante());
  }, []);

  useEffect(() => {
    fetchAll();
    resetModes();
    resetModels();
    resetDependencies();
  }, []);

  const handleActivateInactivate = useOperationController(async (event, id) => {
    event.preventDefault();

    const { active } = tabelasSda.find(
      (item) => item.id === id
    );

    const isActivation = !active;

    if (isActivation) {
      dispatch(activateByIdAsync(id));
    } else {
      dispatch(inactivateByIdAsync(id));
    }
  });

  const handleClickOnEditButton = useCallback(
    (event, id) => {
      event.stopPropagation();

      dispatch(changeToConsultMode());
      history.push(t('com.muralis.qcx.url.modulosGeraisTabelaSDADetalhes', { id }));
    }, [history]
  );
  const breadcrumbs = [
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.cadastros'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloCadastrosGerais'),
        name: t('com.muralis.qcx.gerais'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.servico.labelPlural'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.tabelaSDA.label'),
      },
    },
  ];
  const columns = [
    {
      field: 'id',
      headerName: t('com.muralis.qcx.codigo'),
      align: 'center',
      type: 'string',
      flex: 100,
      valueGetter: ({ row }) => (
        row?.id
      ),
    },
    {
      field: 'description',
      headerName: t('com.muralis.qcx.descricao'),
      flex: 200,
      valueGetter: ({ row }) => (
        row?.description
      ),
    },
    {
      field: 'viaTransporte',
      headerName: t('com.muralis.qcx.transporte.viaTransporte'),
      flex: 130,
      valueGetter: ({ row: { viaTransporte: { description } } }) => description,
    },
    {
      field: 'actions',
      headerName: t('com.muralis.qcx.acoes.label'),
      flex: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row: { id, active } }) => (
        <>
          {userHasDeleteRole(authInfo, 'tabela-sda') && (
          <IconButton
            key={`stw-active${id}`}
            value={active}
            name={`stw-active-${id}`}
            onClick={(event) => handleActivateInactivate(event, id)}
          >
            <DeleteIcon color="error" />
          </IconButton>
          )}
          <IconButton
            key={`btn-view-${id}`}
            name={`btn-view-${id}`}
            onClick={(event) => handleClickOnEditButton(event, id)}
          >
            <PageviewIcon color="primary" />
          </IconButton>
        </>
      ),
    },
  ];

  const handleClickOnButtonNew = useCallback((event) => {
    event.stopPropagation();
    dispatch(changeToCreateMode());
  }, []);

  const templateProps = {
    page: {
      title: t('com.muralis.qcx.tabelaSDA.label'),
      breadcrumbs,
    },
    control: {
      buttonNew: {
        description: t('com.muralis.qcx.tabelaSDA.novaTabelaSDA'),
        path: t('com.muralis.qcx.url.modulosGeraisTabelaSDANovo'),
        onClick: handleClickOnButtonNew,
      },
    },
    table: {
      columns,
    },
  };

  const filterPropGetter = useCallback(
    (tabelaSda) => tabelaSda.description,
    []
  );

  return (
    <QCXSimpleConsultPageTemplate
      pageProps={templateProps.page}
      controlProps={templateProps.control}
      tableProps={templateProps.table}
      tableData={tabelasSda}
      isLoading={isAlternativeLoading}
      filterPropGetter={filterPropGetter}
      buttonNewText={t('com.muralis.qcx.acoes.nova').toUpperCase()}
      authInfo={authInfo}
      requiredRoles={['tabela-sda']}
    />
  );
}
